import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const environmentTarget = {
  kind: "configuration" as const,
  field: "environment",
} as const;

function oauthBase(config: Record<string, string>) {
  return config.environment === "production"
    ? "https://account.docusign.com"
    : "https://account-d.docusign.com";
}

export const docusign = defineConnector({
  identifier: "docusign",
  name: "DocuSign eSignature",
  tagLine: "Electronic signature and agreement management",
  logo,
  protocol: "http",
  tags: ["documents"],
  defaultUrlPattern: () => "https://.*\\.docusign\\.(net|com)/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create an integration key",
      items: [
        {
          kind: "markdown",
          content:
            "In the [DocuSign Apps and Keys](https://admindemo.docusign.com/apps-and-keys) page, create a new app and copy the integration key and secret key.",
        },
      ],
    },
    {
      title: "Select your environment",
      items: [
        {
          kind: "select",
          label: "Environment",
          required: true,
          target: environmentTarget,
          defaultValue: "demo",
          options: [
            { value: "demo", label: "Demo / Sandbox" },
            { value: "production", label: "Production" },
          ],
        },
        {
          kind: "url",
          label: "Account base URI",
          required: true,
          placeholder: "https://na1.docusign.net",
          target: { kind: "configuration", field: "baseUri" },
        },
        {
          kind: "input",
          label: "Account ID",
          required: true,
          target: { kind: "configuration", field: "accountId" },
        },
      ],
    },
    {
      title: "Enter your OAuth credentials",
      items: [
        {
          kind: "input",
          label: "Integration key (client ID)",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Secret key (client secret)",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
        {
          kind: "oauth",
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: (config) => `${oauthBase(config)}/oauth/auth`,
    tokenEndpoint: (config) => `${oauthBase(config)}/oauth/token`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => ["signature", "extended"],
  },
  connectionTest: {
    protocol: "http",
    request: ({ baseUri, accountId }) =>
      `${baseUri}/restapi/v2.1/accounts/${accountId}`,
  },
});
